import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  DateRangePicker,
  Drawer,
  Field,
  PageHead,
  Pager,
  Segmented,
  Select,
  Skeleton,
  StateBlock,
  resolveRangePreset,
  type Column,
  type DateRangePresetId,
} from '@w7/ui'
import { useChildren, useSession } from '@/app/queries'
import { AsOf } from '@/app/bits'
import {
  useCommissionEligibility,
  useCommissionEventBets,
} from '@/api/hooks/reports/useCommissionEligibility'
import {
  COMMISSION_ELIGIBILITY_PAGE_SIZE,
  COMMISSION_MAX_RANGE_DAYS,
  defaultEligibilityWeek,
  type CommissionChildUser,
  type CommissionEligibilityRow,
  type CommissionEligibilityTab,
  type CommissionEventBetRow,
} from '@/api/map/commissionEligibility'
import { CommissionMemberPicker } from '@/features/reports/CommissionMemberPicker'
import { EMPTY, count } from '@/lib/format'
import { ROLE_LABEL, type Account, type Role } from '@/data/types'
import './commission-eligibility.css'

/** Lab commission week presets — calendar popover with From/To date inputs. */
const WEEK_PRESETS: DateRangePresetId[] = ['this-week', 'last-week', 'custom']

/** Lab trigger format: `05.10.2026 - 11.10.2026`. */
export function labWeekSummary(from: string, to: string): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split('-')
    if (!y || !m || !d) return iso
    return `${d}.${m}.${y}`
  }
  if (!from || !to) return 'Select a week'
  return `${fmt(from)} - ${fmt(to)}`
}

export function weekPresetOf(from: string, to: string, today?: Date): DateRangePresetId {
  for (const id of WEEK_PRESETS) {
    if (id === 'custom') continue
    const resolved = resolveRangePreset(id, today)
    if (resolved && resolved.from === from && resolved.to === to) return id
  }
  return 'custom'
}

type Applied = {
  tmId: string
  dmId: string
  smId: string
  masterId: string
  members: CommissionChildUser[]
  from: string
  to: string
  tab: CommissionEligibilityTab
  run: number
}

/** Seat defaults for the signed-in hierarchy level (exported for whitebox tests). */
export function roleSeed(role: Role | undefined, selfId: string): {
  tmId: string
  dmId: string
  smId: string
  masterId: string
} {
  if (!role || !selfId) return { tmId: '', dmId: '', smId: '', masterId: '' }
  if (role === 'triple-master') return { tmId: selfId, dmId: '', smId: '', masterId: '' }
  if (role === 'double-master') return { tmId: '', dmId: selfId, smId: '', masterId: '' }
  if (role === 'super-master') return { tmId: '', dmId: '', smId: selfId, masterId: '' }
  if (role === 'master') return { tmId: '', dmId: '', smId: '', masterId: selfId }
  return { tmId: '', dmId: '', smId: '', masterId: '' }
}

function optionsFor(
  accounts: Account[] | undefined,
  role: Role,
  emptyLabel: string,
): { value: string; label: string }[] {
  return [
    { value: '', label: emptyLabel },
    ...(accounts ?? [])
      .filter((a) => a.role === role)
      .map((a) => ({ value: a.id, label: a.username })),
  ]
}

function deepestScope(tmId: string, dmId: string, smId: string, masterId: string, selfId: string) {
  return masterId || smId || dmId || tmId || selfId
}

/**
 * Commission eligibility — Lab `#!/commission-report` parity for agents.
 *
 * Week + TM→DM→SM→M cascade (scoped to signed-in seat) + member chips +
 * Eligible / Ineligible after Search.
 * Live: Agent-API `POST /v1/account/bqEligibleUsers` (+ bqIneligible / event bets / bqGetChildUser).
 */
export function CommissionEligibilityPage() {
  const { data: session } = useSession()
  const selfId = session?.account.id ?? ''
  const selfRole = session?.account.role
  const selfUsername = session?.account.username ?? ''

  const week0 = useMemo(() => defaultEligibilityWeek(), [])
  const [from, setFrom] = useState(week0.from)
  const [to, setTo] = useState(week0.to)
  const [tmId, setTmId] = useState('')
  const [dmId, setDmId] = useState('')
  const [smId, setSmId] = useState('')
  const [masterId, setMasterId] = useState('')
  const [members, setMembers] = useState<CommissionChildUser[]>([])
  const [tab, setTab] = useState<CommissionEligibilityTab>('eligible')
  const [page, setPage] = useState(1)
  const [eventsFor, setEventsFor] = useState<CommissionEligibilityRow | null>(null)
  const [applied, setApplied] = useState<Applied | null>(null)

  useEffect(() => {
    const next = roleSeed(selfRole, selfId)
    setTmId(next.tmId)
    setDmId(next.dmId)
    setSmId(next.smId)
    setMasterId(next.masterId)
  }, [selfRole, selfId])

  const dmsFromTm = useChildren(selfRole === 'triple-master' ? tmId || undefined : undefined)
  const smsFromDm = useChildren(
    selfRole === 'triple-master' || selfRole === 'double-master' ? dmId || undefined : undefined,
  )
  const mastersFromSm = useChildren(
    selfRole === 'triple-master' || selfRole === 'double-master' || selfRole === 'super-master'
      ? smId || undefined
      : undefined,
  )

  const query = useMemo(
    () => ({
      tripleMaster: applied?.tmId || undefined,
      doubleMaster: applied?.dmId || undefined,
      superMaster: applied?.smId || undefined,
      master: applied?.masterId || undefined,
      scopeId: applied
        ? deepestScope(applied.tmId, applied.dmId, applied.smId, applied.masterId, selfId)
        : deepestScope(tmId, dmId, smId, masterId, selfId),
      memberIds: (applied?.members ?? []).map((m) => m.id),
      from: applied?.from ?? from,
      to: applied?.to ?? to,
      tab: applied?.tab ?? tab,
      page,
      pageSize: COMMISSION_ELIGIBILITY_PAGE_SIZE,
    }),
    [applied, from, to, tab, page, tmId, dmId, smId, masterId, selfId],
  )

  const report = useCommissionEligibility(query, Boolean(applied) && Boolean(query.scopeId))
  const eventBets = useCommissionEventBets(
    {
      from: applied?.from ?? '',
      to: applied?.to ?? '',
      playerId: eventsFor?.id ?? '',
      isEligible: (applied?.tab ?? tab) === 'eligible',
    },
    Boolean(eventsFor && applied),
  )

  const selfOption = useMemo(
    () => (selfId ? [{ value: selfId, label: selfUsername || selfId }] : []),
    [selfId, selfUsername],
  )

  const tmOptions = useMemo(() => {
    if (selfRole === 'triple-master') return selfOption
    return [{ value: '', label: '—' }]
  }, [selfRole, selfOption])

  const dmOptions = useMemo(() => {
    if (selfRole === 'double-master') return selfOption
    if (selfRole === 'triple-master') {
      return optionsFor(dmsFromTm.data, 'double-master', 'Select or search DM…')
    }
    return [{ value: '', label: '—' }]
  }, [selfRole, selfOption, dmsFromTm.data])

  const smOptions = useMemo(() => {
    if (selfRole === 'super-master') return selfOption
    if (selfRole === 'triple-master' || selfRole === 'double-master') {
      return optionsFor(smsFromDm.data, 'super-master', 'Select or search SM…')
    }
    return [{ value: '', label: '—' }]
  }, [selfRole, selfOption, smsFromDm.data])

  const masterOptions = useMemo(() => {
    if (selfRole === 'master') return selfOption
    if (
      selfRole === 'triple-master' ||
      selfRole === 'double-master' ||
      selfRole === 'super-master'
    ) {
      return optionsFor(mastersFromSm.data, 'master', 'Select or search M…')
    }
    return [{ value: '', label: '—' }]
  }, [selfRole, selfOption, mastersFromSm.data])

  const bumpSearch = (nextTab: CommissionEligibilityTab = tab) => {
    setPage(1)
    setApplied({
      tmId,
      dmId,
      smId,
      masterId,
      members,
      from,
      to,
      tab: nextTab,
      run: (applied?.run ?? 0) + 1,
    })
  }

  const clearFilters = () => {
    const week = defaultEligibilityWeek()
    const next = roleSeed(selfRole, selfId)
    setFrom(week.from)
    setTo(week.to)
    setTmId(next.tmId)
    setDmId(next.dmId)
    setSmId(next.smId)
    setMasterId(next.masterId)
    setMembers([])
    setTab('eligible')
    setPage(1)
    setApplied(null)
    setEventsFor(null)
  }

  const columns: Column<CommissionEligibilityRow>[] = [
    {
      id: 'player',
      header: 'Player Name',
      cell: (row) => row.playerName || '—',
    },
    {
      id: 'master',
      header: 'Master Name',
      cell: (row) => row.masterName || '—',
      hideBelow: 720,
    },
    {
      id: 'sm',
      header: 'Super Master',
      cell: (row) => row.superMasterName || '—',
      hideBelow: 900,
    },
    {
      id: 'dm',
      header: 'Double Master',
      cell: (row) => row.doubleMasterName || '—',
      hideBelow: 1100,
    },
    {
      id: 'tm',
      header: 'Triple Master',
      cell: (row) => row.tripleMasterName || '—',
      hideBelow: 1200,
    },
    {
      id: 'events',
      header: 'Events Played',
      numeric: true,
      cell: (row) => (
        <button type="button" className="cm-commission-link" onClick={() => setEventsFor(row)}>
          {count(row.totalEventsPlayed)}
        </button>
      ),
    },
    {
      id: 'bets',
      header: 'Bets Played',
      numeric: true,
      cell: (row) => count(row.totalBetsPlayed),
    },
  ]

  const rows = report.data?.rows ?? []
  const total = report.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / COMMISSION_ELIGIBILITY_PAGE_SIZE) || 1)
  const activeTab = applied?.tab ?? tab
  const drawerEvents: CommissionEventBetRow[] =
    eventBets.data && eventBets.data.length > 0
      ? eventBets.data
      : (eventsFor?.events ?? [])

  const eventColumns: Column<CommissionEventBetRow>[] = [
    {
      id: 'date',
      header: 'Date',
      cell: (row) => row.date || '—',
    },
    {
      id: 'event',
      header: 'Event Name',
      cell: (row) => row.eventName || '—',
    },
    {
      id: 'bets',
      header: 'Bets Played',
      numeric: true,
      cell: (row) => count(row.bets),
    },
  ]

  // Own seat is locked; levels below cascade once the parent is chosen.
  const tmDisabled = true
  const dmDisabled = selfRole !== 'triple-master' || !tmId
  const smDisabled =
    !(selfRole === 'triple-master' || selfRole === 'double-master') || !dmId
  const masterDisabled =
    !(
      selfRole === 'triple-master' ||
      selfRole === 'double-master' ||
      selfRole === 'super-master'
    ) || !smId

  // When the seat *is* that level, keep the locked select enabled visually but non-editable.
  const dmSelectDisabled = selfRole === 'double-master' ? true : dmDisabled
  const smSelectDisabled = selfRole === 'super-master' ? true : smDisabled
  const masterSelectDisabled = selfRole === 'master' ? true : masterDisabled

  return (
    <>
      <PageHead
        title="Commission Report"
        subtitle={`${
          session ? `${session.account.username} · ${ROLE_LABEL[session.account.role]}` : EMPTY
        } — Eligible / Ineligible players for the selected week`}
        actions={report.data && applied ? <AsOf at={report.data.asOf} /> : null}
      />

      <div className="cm-commission-elig">
        <div className="cm-commission-elig-filters" role="region" aria-label="Commission filters">
          <div className="cm-commission-elig-hierarchy">
            <Field label="Select TM">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={tmId}
                  disabled={tmDisabled}
                  onChange={(e) => {
                    setTmId(e.target.value)
                    setDmId('')
                    setSmId('')
                    setMasterId('')
                    setMembers([])
                  }}
                  options={tmOptions}
                />
              )}
            </Field>
            <Field label="Select DM">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={dmId}
                  disabled={dmSelectDisabled}
                  onChange={(e) => {
                    setDmId(e.target.value)
                    setSmId('')
                    setMasterId('')
                    setMembers([])
                  }}
                  options={dmOptions}
                />
              )}
            </Field>
            <Field label="Select SM">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={smId}
                  disabled={smSelectDisabled}
                  onChange={(e) => {
                    setSmId(e.target.value)
                    setMasterId('')
                    setMembers([])
                  }}
                  options={smOptions}
                />
              )}
            </Field>
            <Field label="Select M">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={masterId}
                  disabled={masterSelectDisabled}
                  onChange={(e) => {
                    setMasterId(e.target.value)
                    setMembers([])
                  }}
                  options={masterOptions}
                />
              )}
            </Field>
          </div>

          <CommissionMemberPicker
            selected={members}
            onChange={setMembers}
            tripleMaster={tmId || undefined}
            doubleMaster={dmId || undefined}
            superMaster={smId || undefined}
            master={masterId || undefined}
          />

          <div className="cm-commission-elig-week-row">
            <Field label="Select Week">
              {() => (
                <DateRangePicker
                  label="Select Week"
                  preset={weekPresetOf(from, to)}
                  value={{ from, to }}
                  summary={labWeekSummary(from, to)}
                  presets={WEEK_PRESETS}
                  maxSpanDays={COMMISSION_MAX_RANGE_DAYS}
                  onChange={(next) => {
                    setFrom(next.from)
                    setTo(next.to)
                  }}
                  className="cm-commission-week-trigger"
                />
              )}
            </Field>
            <div className="cm-commission-elig-actions">
              <Button
                variant="primary"
                disabled={!from || !to || report.isFetching}
                busy={report.isFetching}
                onClick={() => bumpSearch(tab)}
              >
                Search
              </Button>
              <Button variant="ghost" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </div>

        {!applied ? (
          <StateBlock icon="commission" title="Choose a week">
            Defaults to last week. Narrow TM → DM → SM → M if needed, then Search — same flow as Lab
            Commission Report.
          </StateBlock>
        ) : report.isLoading ? (
          <Skeleton height={220} />
        ) : report.data?.unavailable ? (
          <StateBlock icon="info" title="Commission eligibility unavailable">
            {report.data.reason ??
              'Hierarchy could not load BigQuery commission eligibility. Demo mode shows sample rows offline.'}
          </StateBlock>
        ) : (
          <>
            <Segmented
              label="Status"
              value={activeTab}
              onChange={(next) => {
                setTab(next as CommissionEligibilityTab)
                setPage(1)
                setApplied((prev) =>
                  prev
                    ? { ...prev, tab: next as CommissionEligibilityTab, run: prev.run + 1 }
                    : prev,
                )
              }}
              items={[
                { id: 'eligible', label: 'Eligible' },
                { id: 'ineligible', label: 'Ineligible' },
              ]}
            />
            <DataTable
              label={`${activeTab === 'eligible' ? 'Eligible' : 'Ineligible'} players`}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={
                <StateBlock icon="commission" title={`No ${activeTab} players`}>
                  Nothing matched this week and scope.
                </StateBlock>
              }
            />
            {total > 0 ? (
              <Pager
                page={page}
                pageCount={pageCount}
                total={total}
                pageSize={COMMISSION_ELIGIBILITY_PAGE_SIZE}
                onChange={setPage}
              />
            ) : null}
          </>
        )}
      </div>

      <Drawer
        open={Boolean(eventsFor)}
        onClose={() => setEventsFor(null)}
        title={eventsFor ? `${eventsFor.playerName} — events` : 'Events'}
        subtitle={
          eventsFor
            ? eventBets.isLoading
              ? 'Loading…'
              : `${count(drawerEvents.reduce((sum, ev) => sum + ev.bets, 0))} bets · ${count(drawerEvents.length)} events`
            : undefined
        }
        width="wide"
      >
        {eventsFor ? (
          eventBets.isLoading ? (
            <Skeleton height={120} />
          ) : (
            <DataTable
              label={`${eventsFor.playerName} events`}
              columns={eventColumns}
              rows={drawerEvents}
              rowKey={(row, index) => row.id || `event-${index}`}
              density="compact"
              pinLastColumn={false}
              stickyOffset={0}
              empty={
                <StateBlock icon="commission" title="No events">
                  No event/day bets for this player in the selected week.
                </StateBlock>
              }
            />
          )
        ) : null}
      </Drawer>
    </>
  )
}
